import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    InputBase,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Chip,
    Grid
} from '@mui/material';
import { CloseOutlinedIcon, SearchOutlinedIcon } from 'icons/materialIcons';
import Modal from 'components/common/Modal';
import { getTemplates } from 'team/apis/teamTemplateApi';
import { useTeam } from 'team/contexts/teamContext';
import TeamMemberDigitalCardPreview from 'team/components/TeamMemberDigitalCardPreview';
import { getCardById } from 'apis/cardApi';
import PropTypes from 'prop-types';

const TeamChooseTemplateModal = ({ open, onClose, onSelectTemplate, onUnassignTemplate, cardId, currentAppliedTemplateId }) => {
    const { currentTeamId } = useTeam();
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [currentCardTheme, setCurrentCardTheme] = useState('#FF7A00');
    const [appliedTemplateId, setAppliedTemplateId] = useState(currentAppliedTemplateId || null);
    const [isUnassigning, setIsUnassigning] = useState(false);

    // Fetch current card theme and applied template when modal opens
    useEffect(() => {
        const fetchCardData = async () => {
            if (!open || !cardId) return;

            try {
                // Always fetch from API to get the latest appliedTemplateId
                const response = await getCardById(cardId);
                if (response && response.card) {
                    if (response.card.cardTheme) {
                        setCurrentCardTheme(response.card.cardTheme);
                    }
                    // Set appliedTemplateId - use null if not set
                    setAppliedTemplateId(response.card.appliedTemplateId || null);
                }
            } catch (error) {
                console.error('Error fetching card data:', error);
            }
        };

        fetchCardData();
    }, [open, cardId]);

    // Update appliedTemplateId when prop changes
    useEffect(() => {
        if (currentAppliedTemplateId !== undefined) {
            setAppliedTemplateId(currentAppliedTemplateId);
        }
    }, [currentAppliedTemplateId]);

    // Fetch templates when modal opens
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!open || !currentTeamId) return;

            setIsLoading(true);
            try {
                const response = await getTemplates(currentTeamId);
                if (response && response.success) {
                    setTemplates(response.templates || []);
                    // Select first template by default if available
                    if (response.templates && response.templates.length > 0) {
                        setSelectedTemplate(response.templates[0]);
                    }
                }
            } catch (error) {
                console.error('Error fetching templates:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTemplates();
    }, [open, currentTeamId]);

    // Filter templates based on search query
    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) {
            return templates;
        }
        const query = searchQuery.toLowerCase();
        return templates.filter(template => {
            const templateName = (template.templateName || '').toLowerCase();
            return templateName.includes(query);
        });
    }, [templates, searchQuery]);

    // Convert template data to card data format for preview
    const cardData = useMemo(() => {
        if (!selectedTemplate) return null;

        const about = selectedTemplate?.about || {};
        return {
            cardFullName: about.cardName || selectedTemplate?.templateName || 'Template Preview',
            cardJobTitle: '',
            cardCompany: '',
            cardPronoun: 'he',
            cardTheme: about.cardTheme || currentCardTheme || '#FF7A00',
            cardProfilePictureUrl: about.profilePictureUrl || '',
            cardCoverPhotoUrl: about.coverPhotoUrl || '',
            cardCompanyLogoUrl: about.companyLogoUrl || '',
            contextCardLayout: about.cardLayout || 'left-aligned',
            cardLinkColor: about.linkColor || currentCardTheme || '#FF7A00',
            cardLocation: about.location || '',
            cardBio: about.bio || '',
            customLinkTypeLabels: selectedTemplate?.links?.customLinkTypeLabels || {}
        };
    }, [selectedTemplate, currentCardTheme]);

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
    };

    const handleApplyTemplate = () => {
        if (selectedTemplate && onSelectTemplate) {
            onSelectTemplate(selectedTemplate);
        }
        onClose();
    };

    const handleUnassignTemplate = async () => {
        if (!onUnassignTemplate || !appliedTemplateId) return;

        setIsUnassigning(true);
        try {
            await onUnassignTemplate();
            setAppliedTemplateId(null);
            onClose();
        } catch (error) {
            console.error('Error unassigning template:', error);
        } finally {
            setIsUnassigning(false);
        }
    };

    // Get the name of the currently applied template
    const appliedTemplateName = useMemo(() => {
        if (!appliedTemplateId || !templates.length) return null;
        const appliedTemplate = templates.find(t => t._id === appliedTemplateId || t._id?.toString() === appliedTemplateId?.toString());
        return appliedTemplate?.templateName || 'Current Template';
    }, [appliedTemplateId, templates]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            maxWidth={900}
            width="70%"
            height="85%"
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: 0
            }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 2,
                    borderBottom: '1px solid #e0e0e0',
                    flexShrink: 0
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Choose a Template
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseOutlinedIcon />
                    </IconButton>
                </Box>

                {/* Main Content - Two Panels */}
                <Box sx={{
                    display: 'flex',
                    flex: 1,
                    overflow: 'hidden',
                    gap: 2,
                    padding: 2
                }}>
                    {/* Left Panel - Template List */}
                    <Box sx={{
                        width: '40%',
                        borderRight: '1px solid #e0e0e0',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        {/* Search Bar */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                backgroundColor: '#f7f7f7',
                                borderRadius: '20px',
                                border: '1px solid #e3e3e3',
                                padding: '8px 16px',
                                marginBottom: 2,
                                '&:hover': {
                                    boxShadow: 2,
                                },
                            }}
                        >
                            <SearchOutlinedIcon
                                sx={{
                                    color: '#828282',
                                    fontSize: '18px',
                                    marginRight: '8px'
                                }}
                            />
                            <InputBase
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search templates..."
                                sx={{
                                    fontSize: '0.7rem',
                                    color: '#4f4f4f',
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#828282',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>

                        {/* Template List */}
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                                    <CircularProgress size={40} />
                                </Box>
                            ) : filteredTemplates.length === 0 ? (
                                <Box sx={{ padding: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#828282' }}>
                                        {searchQuery ? 'No templates found matching your search' : 'No templates available'}
                                    </Typography>
                                </Box>
                            ) : (
                                <List sx={{ padding: 0 }}>
                                    {filteredTemplates.map((template) => (
                                        <ListItem
                                            key={template._id}
                                            onClick={() => handleTemplateSelect(template)}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: selectedTemplate?._id === template._id ? '#f0f0f0' : 'transparent',
                                                borderRadius: '8px',
                                                marginBottom: 1,
                                                padding: '12px 16px',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '0.875rem',
                                                                color: selectedTemplate?._id === template._id ? '#000' : '#4f4f4f'
                                                            }}
                                                        >
                                                            {template.templateName || 'Unnamed Template'}
                                                        </Typography>
                                                        {template.isDefault && (
                                                            <Chip
                                                                label="Default"
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#e3f2fd',
                                                                    color: '#1976d2',
                                                                    fontSize: '0.7rem',
                                                                    height: '20px',
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    template.assignedMembers && template.assignedMembers.length > 0 ? (
                                                        <Typography variant="caption" sx={{ color: '#828282', fontSize: '0.75rem' }}>
                                                            {`${template.assignedMembers.length} member${template.assignedMembers.length === 1 ? '' : 's'}`}
                                                        </Typography>
                                                    ) : null
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Box>

                    {/* Right Panel - Card Preview */}
                    <Box sx={{
                        width: '60%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: 2,
                        paddingTop: 2,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        height: '100%'
                    }}>
                        {selectedTemplate ? (
                            <Box sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                paddingTop: 0,
                                minHeight: 'fit-content'
                            }}>
                                <TeamMemberDigitalCardPreview
                                    cardId="template-preview"
                                    cardData={cardData}
                                    contactLinks={[]}
                                    disableLeadCapture={true}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', padding: 4 }}>
                                <Typography variant="body2" sx={{ color: '#828282' }}>
                                    Select a template to preview
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Footer - Action Buttons */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 2,
                    borderTop: '1px solid #e0e0e0',
                    flexShrink: 0
                }}>
                    {/* Left side - Unassign link (only shown if template is applied) */}
                    <Box>
                        {appliedTemplateId && onUnassignTemplate && (
                            <Box
                                component="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnassignTemplate();
                                }}
                                disabled={isUnassigning}
                                sx={{
                                    padding: 0,
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: '#dc3545', // red text
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    cursor: isUnassigning ? 'not-allowed' : 'pointer',
                                    textTransform: 'none',
                                    opacity: isUnassigning ? 0.6 : 1,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: isUnassigning ? 'none' : 'underline',
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                {isUnassigning ? (
                                    <>
                                        <CircularProgress size={14} sx={{ color: '#dc3545', mr: 0.5 }} />
                                        Unassigning...
                                    </>
                                ) : (
                                    'Unassign Template'
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Right side - Cancel and Apply buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box
                            component="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            sx={{
                                padding: '10px 24px',
                                borderRadius: '20px',
                                border: '1px solid #e3e3e3',
                                backgroundColor: 'white',
                                color: '#4f4f4f',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            Cancel
                        </Box>
                        <Box
                            component="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleApplyTemplate();
                            }}
                            disabled={!selectedTemplate}
                            sx={{
                                padding: '10px 32px',
                                borderRadius: '20px',
                                border: 'none',
                                backgroundColor: selectedTemplate ? '#000' : '#e0e0e0',
                                color: selectedTemplate ? '#fff' : '#a6a6a6',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                cursor: selectedTemplate ? 'pointer' : 'not-allowed',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: selectedTemplate ? '#333' : '#e0e0e0',
                                },
                            }}
                        >
                            Apply Template
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

TeamChooseTemplateModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelectTemplate: PropTypes.func.isRequired,
    onUnassignTemplate: PropTypes.func,
    cardId: PropTypes.string,
    currentAppliedTemplateId: PropTypes.string
};

export default TeamChooseTemplateModal;

